import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { LayoutSingleColumn, NamedLink, Page } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { createHandymanSocialSharing } from '../../util/handymanSocialSharing';
import { buildHandymanLocalBusinessSchema } from '../../util/handymanLocalBusinessSchema';
import { propTypes } from '../../util/types';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import CleaningListingsSection from './CleaningListingsSection';
import { createCleaningMapStateToProps } from './cleaningPageState';
import { OPEN_QUOTE_EVENT } from '../../components/QuoteChat/QuoteChat';
import css from '../HandymanPage/HandymanPage.module.css';
import { cleaningCityLinks } from '../../data/cleaningSeoPages';
import { linkifyCleaningServices } from '../../data/cleaningServiceLinkifier';

const HERO_IMAGE = '/static/services/cleaning/cleaning-hero-kitchen-window.jpg';

// Open the AI intake chat, optionally seeding the first message.
const openQuoteChat = prefill => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPEN_QUOTE_EVENT, { detail: prefill ? { prefill } : {} }));
  }
};

const contentSections = [
  {
    title: 'What cleaning jobs look like across Ontario',
    paragraphs: [
      'Most bookings are practical: standard house cleaning every week or two, a one-time deep reset between seasons, a move-out clean before keys are handed back, condo turnovers, and short-term rental cleanups between guests. The job is rarely complicated — what matters is consistency, attention to detail, and a cleaner who shows up on time.',
      'Many households use FixitTask to find the same cleaner for a recurring schedule. That tends to produce better results than rotating providers, because the cleaner learns the home and remembers the small details from visit to visit.',
    ],
  },
  {
    title: 'Who books cleaning help in Ontario',
    paragraphs: [
      'Busy professionals and families who want a recurring weekly or bi-weekly visit so the home stays at a stable baseline. Condo owners and renters in downtown Toronto, Mississauga, and Ottawa who need a one-time reset or pre-guest clean.',
      'Landlords running turnovers between tenants, sellers preparing a home for listing photos, and Airbnb hosts coordinating same-day guest-to-guest turnovers. Each scenario has its own scope — the booking page for that service has the specifics.',
    ],
  },
  {
    title: 'How to book a cleaner in Ontario',
    paragraphs: [
      'Browse listings, read recent reviews, then message the cleaner directly. You agree on scope, frequency, supplies, and timing before anything is booked. No agency in the middle.',
      'Be specific in your first message: square footage or room count, how long since the last clean, any pets, surfaces that need particular care (hardwood, quartz, stone), and access details if you live in a condo with elevator booking rules.',
    ],
  },
  {
    title: 'Choosing the right cleaner for your home',
    paragraphs: [
      "Look for reviews from visits similar to yours — condo turnovers, family-home recurring schedules, deep cleans, move-outs. Each has a different rhythm, and reviewers tend to mention what they cared about most.",
      'If you have product preferences (fragrance-free, eco-friendly, pet-safe), mention it up front. Many Ontario cleaners can adjust or bring alternatives, but only if they know before the visit.',
    ],
  },
];

const faqs = [
  {
    question: 'What kinds of cleaning services can I find on FixitTask?',
    answer:
      'Standard house cleaning, deep cleaning, move-in and move-out visits, condo cleaning, Airbnb and short-term rental turnovers, recurring weekly or bi-weekly bookings, post-construction cleanups, and one-time resets after holidays or guest stays.',
  },
  {
    question: 'How much does cleaning cost in Ontario?',
    answer:
      'Standard house cleaning typically runs $35–$60/hour for individual cleaners, with flat-rate visits for one-bedroom condos starting around $120 and family homes from $180. Deep cleans cost 1.5–2× standard. Recurring weekly or bi-weekly bookings are usually 10–20% less per visit than a one-time clean.',
  },
  {
    question: 'Do Ontario cleaners bring their own supplies?',
    answer:
      "Many do — and many don't. Confirm in chat before the first visit. Cleaners who use your products usually charge slightly less. Mention any product sensitivities, pets, or specific surfaces (hardwood, quartz, stone, vintage trim) when you message.",
  },
  {
    question: 'Can I book the same cleaner every week?',
    answer:
      'Yes. Most cleaners on FixitTask offer weekly, bi-weekly, or monthly recurring bookings with the same person each time. Recurring visits cost less per visit and produce more consistent results.',
  },
];

export const CleaningPageComponent = props => {
  const { listings, queryInProgress, queryListingsError } = props;
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const rootUrl = config?.marketplaceRootURL || 'https://fixittask.com';

  const createSearchPath = params =>
    createResourceLocatorString('SearchPage', routeConfiguration, {}, params);

  // "See more cleaners" should land on the search page pre-filtered to cleaning.
  const browseAllPath = createSearchPath({ pub_categoryLevel1: 'Cleaning_Services' });

  const quotePrefill = 'I need cleaning in Ontario';

  // No live listings yet → invite into the chat instead of a dead-end.
  const listingsEmptyState = (
    <div className={css.listingsState}>
      <h3 className={css.cardTitle}>Tell us what you need — we’ll find you a cleaner</h3>
      <p className={css.cardText}>
        New cleaners join every week. Describe the job (a photo helps) and we’ll match you with a
        trusted local pro and call you back shortly.
      </p>
      <button type="button" className={css.quoteButton} onClick={() => openQuoteChat(quotePrefill)}>
        Get a free quote
      </button>
    </div>
  );

  const title = 'Cleaning Services in Ontario | Local Cleaners | FixitTask';
  const description =
    'Find local cleaners across Ontario on FixitTask. Compare listings, reviews, and prices for house cleaning, deep cleans, move-outs, condo turnovers, and recurring visits.';

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Cleaning Services in Ontario',
      description,
      url: `${rootUrl}/cleaning`,
    },
    buildHandymanLocalBusinessSchema({
      rootUrl,
      pageUrl: `${rootUrl}/cleaning`,
      name: 'Cleaning Services in Ontario',
      description,
      image: `${rootUrl}${HERO_IMAGE}`,
      cities: cleaningCityLinks.map(item => item.city),
      region: 'Ontario',
    }),
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: rootUrl },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Cleaning',
          item: `${rootUrl}/cleaning`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ];

  return (
    <Page
      title={title}
      description={description}
      socialSharing={createHandymanSocialSharing(rootUrl, title, description)}
      schema={schema}
      className={css.root}
    >
      <Helmet>
        <link rel="canonical" href={`${rootUrl}/cleaning`} />
      </Helmet>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.page}>
          <section className={css.hero} style={{ backgroundImage: `url(${HERO_IMAGE})` }}>
            <div className={css.heroOverlay}>
              <div className={css.heroContent}>
                <nav className={css.breadcrumbs} aria-label="Breadcrumb">
                  <NamedLink name="LandingPage" className={css.breadcrumbLink}>
                    Home
                  </NamedLink>
                  <span aria-hidden="true">/</span>
                  <span>Cleaning</span>
                </nav>

                <p className={css.eyebrow}>Trusted local cleaners across Ontario</p>
                <h1 className={css.title}>Cleaning Services in Ontario</h1>
                <p className={css.lead}>
                  Find local cleaners on FixitTask for house cleaning, deep cleans, move-out
                  visits, condo turnovers, Airbnb resets, and the recurring help that keeps homes
                  and rentals running.
                </p>

                <div className={css.heroCta}>
                  <button
                    type="button"
                    className={css.quoteButton}
                    onClick={() => openQuoteChat(quotePrefill)}
                  >
                    Get a free quote
                  </button>
                  <ul className={css.trustRow}>
                    <li>Reply in minutes</li>
                    <li>Vetted local cleaners</li>
                    <li>No sign-up, no obligation</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <CleaningListingsSection
            heading="Find the right cleaner near you"
            text="Discover trusted local cleaners, compare prices and real reviews, chat directly before booking, and confidently choose the option that fits your home."
            listings={listings}
            queryInProgress={queryInProgress}
            queryListingsError={queryListingsError}
            browseAllPath={browseAllPath}
            emptyStateChildren={listingsEmptyState}
          />

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Why customers use cleaning services</p>
              <h2 className={css.sectionTitle}>
                Cleaning help for homes, condos, rentals, and short-term units
              </h2>
              <p className={css.sectionText}>
                Use this page when you need a practical starting point for finding reliable cleaning
                help across Ontario.
              </p>
            </div>

            <div className={css.contentGrid}>
              {contentSections.map(section => (
                <article className={css.contentCard} key={section.title}>
                  <h3 className={css.contentTitle}>{section.title}</h3>
                  {section.paragraphs.map(paragraph => (
                    <p className={css.contentParagraph} key={paragraph}>
                      {linkifyCleaningServices(paragraph, null)}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </section>

          <section className={css.section}>
            <div className={css.cityPanel}>
              <div className={css.sectionHeader}>
                <p className={css.sectionEyebrow}>Popular Ontario cities</p>
                <h2 className={css.sectionTitle}>Browse cleaning help by city</h2>
                <p className={css.sectionText}>
                  Start with a major Ontario city if you already know where the visit needs to
                  happen.
                </p>
              </div>

              <div className={css.cityGrid}>
                {cleaningCityLinks.map(item => (
                  <a className={css.cityLink} href={item.path} key={item.slug}>
                    <span>{item.city}</span>
                    <span className={css.cityAction}>Browse</span>
                  </a>
                ))}
              </div>
            </div>
          </section>

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>FAQ</p>
              <h2 className={css.sectionTitle}>Questions customers usually have</h2>
            </div>

            <div className={css.faqGrid}>
              {faqs.map(item => (
                <article className={css.faqCard} key={item.question}>
                  <h3 className={css.cardTitle}>{item.question}</h3>
                  <p className={css.cardText}>{linkifyCleaningServices(item.answer, null)}</p>
                </article>
              ))}
            </div>
          </section>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

CleaningPageComponent.defaultProps = {
  listings: [],
  queryListingsError: null,
};

const { arrayOf, bool } = PropTypes;

CleaningPageComponent.propTypes = {
  listings: arrayOf(propTypes.listing),
  queryInProgress: bool.isRequired,
  queryListingsError: propTypes.error,
};

const CleaningPage = compose(connect(createCleaningMapStateToProps('CleaningPage')))(
  CleaningPageComponent
);

export default CleaningPage;
